/** Pure derivation + CSV-row helpers for the /gis-queue Worklists tab and the
 *  queue's CSV exports (UX remediation Stage 3, C3-rest / decision D-8).
 *
 *  The silent-meter derivation is CLIENT-side: the capacity endpoint
 *  (GET /engineering/capacity/transformers?load_source=measured) returns every
 *  field it needs and is unpaginated. The unrooted list is filtered SERVER-side
 *  (GET /secondary/review-queue?why_flagged=unrooted, Task 10.1) because that
 *  endpoint pages at 200 with no reason precedence in its ordering;
 *  `unrootedRows` below stays as the client-side guard + sort, not as the
 *  filter of record. Both are presentation/aggregation only (spec §1).
 *
 *  CSV rows are plain Record<string, unknown> for the shared
 *  utils/csv.ts::exportCsv(filename, rows) contract. */

import type { TransformerLoadingResult } from '../../types/engineering.ts'
import type { FedByFinding } from '../../types/fedBy.ts'
import type {
  PoleDisagreement,
  PoleWithoutTransformer,
  SecondaryDisagreement,
} from '../../types/linkEvidence.ts'
import type { ReviewQueueItem } from '../../types/secondaryReview.ts'
import type { ChangeRequestSummary, Finding } from './types.ts'

/** Measured-basis rows with silent meters, most-silent first (ties: highest
 *  effective loading first). The `loading_basis !== 'model'` guard is
 *  load-bearing: on model-fallback rows `meters_missing` is the schema
 *  default (schemas/engineering.py: `meters_missing: int = 0`), not a
 *  measurement, so only measured/hybrid rows may qualify. */
export function silentMeterRows(
  transformers: TransformerLoadingResult[],
): TransformerLoadingResult[] {
  return transformers
    .filter(t => t.loading_basis !== 'model' && t.meters_missing > 0)
    .sort(
      (a, b) =>
        b.meters_missing - a.meters_missing ||
        (b.effective_loading_pct ?? 0) - (a.effective_loading_pct ?? 0),
    )
}

/** Review-queue rows flagged 'unrooted' (a secondary run serving meters but
 *  connected to no transformer), most meters first. The server already applies
 *  this filter (`why_flagged=unrooted`, Task 10.1); keeping it here is the
 *  belt-and-braces guard and — the load-bearing part — the sort. */
export function unrootedRows(items: ReviewQueueItem[]): ReviewQueueItem[] {
  return items
    .filter(c => c.why_flagged === 'unrooted')
    .sort((a, b) => b.meter_count - a.meter_count)
}

/** `<base>-YYYY-MM-DD.csv` — exports pile up in Downloads; the date keeps
 *  re-exports distinguishable. `now` is injectable for tests only. */
export function csvFilename(base: string, now: Date = new Date()): string {
  return `${base}-${now.toISOString().slice(0, 10)}.csv`
}

/** CSV cells must be scalars; objects (e.g. a relink CR's proposed_value)
 *  are JSON-stringified, matching CrPanel's formatValue display idiom. */
function scalar(v: unknown): unknown {
  if (v !== null && typeof v === 'object') return JSON.stringify(v)
  return v
}

/** Queue findings → flat rows (the nested `discrepancy` unpacked). */
export function findingCsvRows(findings: Finding[]): Record<string, unknown>[] {
  return findings.map(f => ({
    id: f.discrepancy.id,
    type: f.discrepancy.discrepancy_type,
    table: f.discrepancy.table_name,
    record_id: f.discrepancy.record_id,
    gis_id: f.discrepancy.gis_id,
    field: f.discrepancy.field_name,
    platform_value: f.discrepancy.platform_value,
    gis_value: f.discrepancy.gis_value,
    entity: f.entity_summary,
    age_days: f.age_days,
    import_run_id: f.discrepancy.import_run_id,
    created_at: f.discrepancy.created_at,
  }))
}

/** Change-request summaries → flat rows. */
export function crCsvRows(items: ChangeRequestSummary[]): Record<string, unknown>[] {
  return items.map(cr => ({
    id: cr.id,
    kind: cr.kind,
    status: cr.status,
    entity_type: cr.entity_type,
    entity_id: cr.entity_id,
    field: cr.field_name,
    proposed_value: scalar(cr.proposed_value),
    origin: cr.origin,
    created_by: cr.created_by,
    created_at: cr.created_at,
    target_key: cr.target_key,
  }))
}

/** Silent-meter worklist rows → the columns a GIS editor needs to chase a
 *  wrong meter link or dead meter. */
export function silentMeterCsvRows(
  rows: TransformerLoadingResult[],
): Record<string, unknown>[] {
  return rows.map(t => ({
    transformer_id: t.id,
    name: t.name,
    gis_id: t.gis_id,
    feeder: t.feeder_name,
    kva_rating: t.kva_rating,
    meters: t.meter_count,
    silent_meters: t.meters_missing,
    measured_peak_kw: t.measured_peak_kw,
    effective_loading_pct: t.effective_loading_pct,
    loading_basis: t.loading_basis,
  }))
}

/** Unrooted-component worklist rows. `anchor_segment_id` is the import-stable
 *  cross-run key (types/secondaryReview.ts) — the value a GIS editor can
 *  actually find again after a recompute renumbers component_ids. */
export function unrootedCsvRows(items: ReviewQueueItem[]): Record<string, unknown>[] {
  return items.map(c => ({
    run_id: c.run_id,
    component_id: c.component_id,
    feeder: c.feeder_name,
    feeder_id: c.feeder_id,
    anchor_segment_id: c.anchor_segment_id,
    meter_count: c.meter_count,
    why_flagged: c.why_flagged,
    candidates: c.candidates
      .map(cand => `${cand.transformer_name ?? 'transformer'} #${cand.transformer_id}`)
      .join('; '),
  }))
}

/** Fed-by findings -> flat CSV rows (spec 2026-09-02 Phase 4.4). Column names
 *  are the plain-language ones the GIS editor reads, not the API's field names:
 *  `pointer_says` is the device the map data names, `model_says` is the device
 *  the protection run computed. `pointer_feeder_id` (other_feeder rows) and
 *  `reason` (unresolved rows) ride along so the exported line is actionable
 *  without a second lookup; both are blank on every other classification. */
export function fedByCsvRows(items: FedByFinding[]): Record<string, unknown>[] {
  return items.map(f => ({
    id: f.id,
    run_id: f.run_id,
    feeder: f.feeder_name,
    kind: f.entity_type,
    entity_id: f.entity_id,
    entity: f.entity_name,
    field: f.pointer_field,
    pointer_raw: f.pointer_raw,
    pointer_says: f.pointer_device_name ?? f.pointer_device_id,
    pointer_feeder_id: f.pointer_feeder_id ?? null,
    model_says: f.computed_device_name ?? f.computed_device_id,
    classification: f.classification,
    reason: f.reason ?? null,
    distance_m: f.distance_m,
  }))
}

/** Secondary-disagreement worklist rows (spec 2026-09-02 section 6 Phase 5.2).
 *  Column names are the plain-language ones the GIS editor reads:
 *  `linked_transformer` / `secondary_says` prefer the transformer's name and
 *  fall back to its id when unnamed; `already_decided` surfaces the run-aware
 *  override flag so a re-exported row doesn't read as still-open work. */
export function secondaryDisagreementCsvRows(items: SecondaryDisagreement[]): Record<string, unknown>[] {
  return items.map(i => ({
    run_id: i.run_id, meter_id: i.meter_id, meter_number: i.meter_number, gis_id: i.gis_id,
    feeder: i.feeder_name, tier: i.tier,
    linked_transformer: i.linked_transformer_name ?? i.linked_transformer_id,
    secondary_says: i.secondary_transformer_name ?? i.secondary_transformer_id,
    component_id: i.component_id, component_meters: i.component_meter_count,
    already_decided: i.override_active ? 'yes' : 'no',
    distance_to_linked_m: i.distance_to_linked_m == null ? null : Math.round(i.distance_to_linked_m),
  }))
}

/** Pole-disagreement worklist rows (spec 2026-09-02 section 6 Phase 5.2).
 *  `pole_says` joins every transformer PT_XFMR_LOC names on this pole into one
 *  cell, by index against `pole_transformer_ids` so an unnamed transformer
 *  still reads as `#<id>` rather than dropping out of the row. */
export function poleDisagreementCsvRows(items: PoleDisagreement[]): Record<string, unknown>[] {
  return items.map(i => ({
    meter_id: i.meter_id, meter_number: i.meter_number, gis_id: i.gis_id, feeder: i.feeder_name,
    pole: i.pole_ref,
    linked_transformer: i.linked_transformer_name ?? i.linked_transformer_id ?? null,
    pole_says: i.pole_transformer_names.map((n, k) => n ?? `#${i.pole_transformer_ids[k]}`).join('; '),
    distance_to_linked_m: i.distance_to_linked_m == null ? null : Math.round(i.distance_to_linked_m),
  }))
}

/** Poles-without-transformer worklist rows (spec 2026-09-02 section 6 Phase
 *  5.2) -- poles PT_XFMR_LOC names that match no active platform transformer. */
export function polesWithoutTransformerCsvRows(items: PoleWithoutTransformer[]): Record<string, unknown>[] {
  return items.map(i => ({
    pole: i.pole_ref, meters: i.meter_count, feeder: i.feeder_name,
    sample_meter_ids: i.sample_meter_ids.join('; '),
  }))
}
